import { useEffect, useLayoutEffect, useRef } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'

/**
 * Remembered window scroll offsets, keyed by react-router history entry.
 *
 * A module-level Map (not state) so positions survive component remounts and
 * the entries stay tied to a single browser session's history keys.
 */
const scrollPositions = new Map<string, number>()

/**
 * Restores the window scroll position when navigating back/forward (POP) and
 * scrolls to the top on a fresh navigation (PUSH/REPLACE) — the behaviour the
 * browser gives on a full page load, which client-side routing otherwise loses.
 *
 * Mount this once in the app shell (see Main.tsx). List pages already persist
 * their filters/pagination via `location.state`; this fills in the missing
 * piece so you land back where you were scrolled instead of at the top.
 *
 * The window/body is the scroll container (AdminLTE default layout); there is
 * no inner overflow element to track.
 */
export function useScrollRestoration(): void {
  const location = useLocation()
  const navigationType = useNavigationType()

  // Key of the history entry currently on screen. Kept in a ref so the single
  // persistent scroll listener always writes to the active entry.
  const activeKeyRef = useRef(location.key)

  // While we programmatically restore, suppress the scroll listener so the
  // restore itself doesn't overwrite a remembered position with an in-between
  // value.
  const restoringRef = useRef(false)

  useEffect(() => {
    const handleScroll = () => {
      if (!restoringRef.current) {
        scrollPositions.set(activeKeyRef.current, window.scrollY)
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useLayoutEffect(() => {
    activeKeyRef.current = location.key
    restoringRef.current = true

    const target = navigationType === 'POP'
      ? scrollPositions.get(location.key) ?? 0
      : 0

    let rafId = 0
    let frames = 0

    const apply = () => {
      window.scrollTo(0, target)
      frames += 1

      // Page content is frequently loaded async (TanStack Query), so the
      // document may still be growing to its full height. Keep nudging until we
      // reach the target or ~1s of frames has passed, then stop suppressing.
      if (window.scrollY < target && frames < 60) {
        rafId = requestAnimationFrame(apply)
      }
      else {
        restoringRef.current = false
      }
    }

    apply()

    return () => {
      cancelAnimationFrame(rafId)
      restoringRef.current = false
    }
  }, [location.key, navigationType])
}
